import { z } from "zod";
import { botConfigV1Schema } from "./v1.js";
import { botConfigV2Schema, migrateV1ToV2, type BotConfigV2 } from "./v2.js";

/**
 * `bot_versions.config` is versioned, immutable history — rollback can
 * re-point `published_version_id` at a row written several schema
 * revisions ago. Every read goes through `migrateConfig`, a forward-only
 * chain: each step turns version N's raw shape into version N+1's raw
 * shape (unvalidated), and only the final schema actually parses. Writes
 * always stamp `CURRENT_CONFIG_SCHEMA_VERSION`.
 *
 * Adding v3: add `botConfigV3Schema`, register it below, add a
 * `2: migrateV2ToV3` step to `migrations`, bump
 * `CURRENT_CONFIG_SCHEMA_VERSION`, and add a v2 fixture to the test suite
 * (test 23 in PLAN.md) so old rows keep loading.
 */
export const CURRENT_CONFIG_SCHEMA_VERSION = 2;

export type BotConfig = BotConfigV2;

/**
 * The shape a config has once it has been migrated — what every reader sees.
 *
 * Exported so DTOs can say "the current config" rather than naming a version
 * that will be wrong at the next bump: the panel is explicitly not supposed
 * to know a row's `config_schema_version`, and a hard-coded `botConfigV1Schema`
 * in a DTO is how it silently starts to.
 */
export const currentBotConfigSchema = botConfigV2Schema;

type ConfigMigration = (raw: unknown) => unknown;

const schemasByVersion: Record<number, z.ZodTypeAny> = {
  1: botConfigV1Schema,
  2: botConfigV2Schema,
};

const migrations: Record<number, ConfigMigration> = {
  1: migrateV1ToV2,
};

export class ConfigMigrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ConfigMigrationError";
  }
}

export function migrateConfig(schemaVersion: number, raw: unknown): BotConfig {
  let version = schemaVersion;
  let data = raw;

  while (version < CURRENT_CONFIG_SCHEMA_VERSION) {
    const migrate = migrations[version];
    if (!migrate) {
      throw new ConfigMigrationError(`no migration registered from config schema version ${version}`);
    }
    data = migrate(data);
    version += 1;
  }

  const schema = schemasByVersion[CURRENT_CONFIG_SCHEMA_VERSION];
  if (!schema) {
    throw new ConfigMigrationError(`no schema registered for config schema version ${CURRENT_CONFIG_SCHEMA_VERSION}`);
  }
  return schema.parse(data) as BotConfig;
}
